import {
  ClassBreakType,
  QuantityClass,
  QuantitySymbologyConfig,
  RangeLowerOp,
  RangeUpperOp,
  SingleSymbologyConfig,
  SymbologyConfig,
} from '../../../../types/symbology.types';
import { calculateClassBreaks, pickColorsForBreaks } from '../../../../shared/utils/quantitySymbologyHelpers';
import { COLOR_RAMPS } from '../constants';

// US-101669 Scenario 6 / US-101670 validation messages — wording matches ACs.
export const MIN_CLASSES = 2;
export const MAX_CLASSES = 9;
export const NUMBER_OF_CLASSES_ERROR = 'Number of classes must be between 2 and 9';
export const MIN_CLASS_BREAKS_ERROR = 'At least 2 class breaks are required';
export const QUANTITY_FIELD_REQUIRED_ERROR = 'Select a field to configure quantity symbology';
export const QUANTITY_CLASSES_PENDING_ERROR = 'Class breaks are being generated. Please try again';

export const RANGE_ERRORS = {
  DECIMAL: 'Decimal value is not accepted', // Kept for backwards compatibility if needed elsewhere, but no longer triggered
  INCOMPLETE: 'Range expression is incomplete',
  INVALID: 'Invalid range expression',
  REVERSED: 'Range start value must be less than range end value',
  OVERLAP: 'Range overlaps with an existing class break',
  DUPLICATE: 'Duplicate range is not allowed',
} as const;

export interface ParsedRange {
  min: number; // -Infinity when unbounded below
  max: number; // +Infinity when unbounded above
  minOp: RangeLowerOp;
  maxOp: RangeUpperOp;
}

export type ParseResult = (ParsedRange & { ok: true }) | { ok: false; error: string | null };

interface Condition {
  op: '>' | '>=' | '<' | '<=' | '=';
  value: number;
}

const parseCondition = (raw: string): Condition | { error: string } => {
  const trimmed = raw.trim();

  // Regex supports operators and numbers (including decimals and negatives)
  const match = trimmed.match(/^(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)$/);

  if (!match) {
    // Check if it starts with an operator but is missing/has an invalid number
    const startsWithOp = /^(>=|<=|>|<|=)/.test(trimmed);
    if (startsWithOp) return { error: RANGE_ERRORS.INCOMPLETE }; // e.g. ">", ">="

    return { error: RANGE_ERRORS.INVALID }; // no leading operator or invalid chars (e.g. != 10, ~10, text)
  }

  const op = match[1] as Condition['op'];
  const valueString = match[2];

  // Use parseFloat instead of parseInt to support decimal flood data
  return { op, value: parseFloat(valueString) };
};

/**
 * Parse a Manual range expression (US-101670 Scenarios 2-9). Blank input is
 * neutral (no error). Returns numeric bounds + operators on success.
 */
export const parseRangeExpression = (expr: string | null | undefined): ParseResult => {
  const trimmed = (expr ?? '').trim();
  if (trimmed === '') return { ok: false, error: null };

  const parts = trimmed.split(/\s+AND\s+/i);
  if (parts.length > 2) return { ok: false, error: RANGE_ERRORS.INVALID };

  const conds: Condition[] = [];
  for (const part of parts) {
    const c = parseCondition(part);
    if ('error' in c) return { ok: false, error: c.error };
    conds.push(c);
  }

  if (conds.length === 1) {
    const { op, value } = conds[0];
    switch (op) {
      case '=':
        return { ok: true, min: value, max: value, minOp: '>=', maxOp: '<=' };
      case '>':
        return { ok: true, min: value, max: Infinity, minOp: '>', maxOp: null };
      case '>=':
        return { ok: true, min: value, max: Infinity, minOp: '>=', maxOp: null };
      case '<':
        return { ok: true, min: -Infinity, max: value, minOp: null, maxOp: '<' };
      case '<=':
        return { ok: true, min: -Infinity, max: value, minOp: null, maxOp: '<=' };
    }
  }

  // Two conditions: exactly one lower bound and one upper bound.
  const lower = conds.find((c) => c.op === '>' || c.op === '>=');
  const upper = conds.find((c) => c.op === '<' || c.op === '<=');
  if (!lower || !upper) return { ok: false, error: RANGE_ERRORS.INVALID }; // both same side, or contains "="
  if (lower.value > upper.value) return { ok: false, error: RANGE_ERRORS.REVERSED };
  return {
    ok: true,
    min: lower.value,
    max: upper.value,
    minOp: lower.op as RangeLowerOp,
    maxOp: upper.op as RangeUpperOp,
  };
};

const sameRange = (a: ParsedRange, b: ParsedRange) => a.min === b.min && a.max === b.max && a.minOp === b.minOp && a.maxOp === b.maxOp;

// Do two (inclusive-aware) intervals share at least one value?
const intervalsOverlap = (a: ParsedRange, b: ParsedRange): boolean => {
  // a entirely left of b?
  const aLeftOfB = a.max < b.min || (a.max === b.min && !(a.maxOp === '<=' && b.minOp === '>='));
  const bLeftOfA = b.max < a.min || (b.max === a.min && !(b.maxOp === '<=' && a.minOp === '>='));
  return !(aLeftOfB || bLeftOfA);
};

/**
 * US-101670 Scenarios 7 & 8 / 10: detect duplicate or overlapping class
 * ranges. Gaps are allowed (no error). Only classes with a valid parsed range
 * participate. Returns the AC error message, or null when conflict-free.
 */
export const detectRangeConflict = (classes: QuantityClass[]): string | null => {
  const parsed = classes.map((c) => parseRangeExpression(c.expression)).filter((p): p is ParsedRange & { ok: true } => p.ok);
  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      if (sameRange(parsed[i], parsed[j])) return RANGE_ERRORS.DUPLICATE;
      if (intervalsOverlap(parsed[i], parsed[j])) return RANGE_ERRORS.OVERLAP;
    }
  }
  return null;
};

// Removed Math.round(n) so decimal values aren't stripped from auto-generated text
const fmt = (n: number): string => (Number.isFinite(n) ? String(n) : '');

/** Build a readable range expression from numeric bounds + operators. */
export const buildExpression = (min: number, max: number, minOp: RangeLowerOp, maxOp: RangeUpperOp): string => {
  const parts: string[] = [];
  if (minOp) parts.push(`${minOp} ${fmt(min)}`);
  if (maxOp) parts.push(`${maxOp} ${fmt(max)}`);
  return parts.join(' AND ');
};

/** Build a human display label from numeric bounds. */
export const buildLabel = (min: number, max: number): string => `${fmt(min)} - ${fmt(max)}`;

/**
 * A field is chosen but its manual table is still empty. This warns rather than
 * blocks — the user may knowingly save an unsymbolized layer. A partly filled
 * table is configured; its filled classes still draw.
 */
export const quantityHasNoRanges = (config: SymbologyConfig | null | undefined): boolean => {
  if (config?.type !== 'quantity' || !config.field || config.breakType !== 'manual') return false;
  return config.classes.length === 0 || config.classes.every((c) => !(c.expression ?? '').trim());
};

/**
 * Save-time validation for a quantity config (US-101670 Scenarios 4-9 inline
 * errors + 7/8 conflict blocking). Returns the first AC error string, or null
 * when the config is valid and safe to persist.
 */
export const validateQuantityConfig = (config: QuantitySymbologyConfig): string | null => {
  // No field means nothing can be rendered at all — that blocks, unlike an empty
  // manual table (quantityHasNoRanges), which only warns.
  if (!config.field) return QUANTITY_FIELD_REQUIRED_ERROR;
  // Auto break types build their classes from a server-side value sample, so an
  // empty table there means the sample hasn't landed yet rather than user error.
  if (config.breakType !== 'manual' && config.classes.length === 0) return QUANTITY_CLASSES_PENDING_ERROR;

  if (config.breakType === 'manual') {
    for (const c of config.classes) {
      const parsed = parseRangeExpression(c.expression);
      // A non-blank expression that fails to parse blocks the save.
      if (!parsed.ok && parsed.error) return parsed.error;
    }
  }
  return detectRangeConflict(config.classes);
};

const cloneSymbologyWithFill = (base: SingleSymbologyConfig, fill: string): SingleSymbologyConfig => ({
  ...base,
  fill,
  icon: base.icon ? { ...base.icon } : null,
});

const rampColors = (ramp: { colors: string[] }, count: number) => pickColorsForBreaks(ramp.colors, count);

/**
 * US-101669 Scenario 7: auto-generate class breaks from the field values for
 * the chosen break type, number of classes, and ramp.
 */
export const generateAutoClasses = (
  values: number[],
  breakType: ClassBreakType,
  numberOfClasses: number,
  ramp: { id: string; colors: string[] },
  baseSymbology: SingleSymbologyConfig,
): QuantityClass[] => {
  const breaks = calculateClassBreaks(values, breakType, numberOfClasses);
  const colors = rampColors(ramp, breaks.length || numberOfClasses);
  return breaks.map((b, i) => {
    const isLast = i === breaks.length - 1;
    const minOp: RangeLowerOp = '>=';
    const maxOp: RangeUpperOp = isLast ? '<=' : '<';
    const color = colors[i] ?? colors[colors.length - 1];
    return {
      min: b.min,
      max: b.max,
      minOp,
      maxOp,
      color,
      displayLabel: `${fmt(b.min)} - ${fmt(b.max)}`,
      expression: `>= ${fmt(b.min)} AND ${maxOp} ${fmt(b.max)}`,
      symbology: cloneSymbologyWithFill(baseSymbology, color),
    };
  });
};

/** US-101669 Scenario 8: Manual mode starts with blank range rows. */
export const generateManualClasses = (
  numberOfClasses: number,
  ramp: { id: string; colors: string[] },
  baseSymbology: SingleSymbologyConfig,
): QuantityClass[] => {
  const colors = rampColors(ramp, numberOfClasses);
  return Array.from({ length: numberOfClasses }, (_, i) => {
    const color = colors[i] ?? colors[colors.length - 1];
    return {
      min: NaN,
      max: NaN,
      minOp: null,
      maxOp: null,
      color,
      displayLabel: '',
      expression: '',
      symbology: cloneSymbologyWithFill(baseSymbology, color),
    } as QuantityClass;
  });
};

/** Recolor existing classes from a ramp (US-101669 Scenario 9), keeping ranges/symbology. */
export const applyRampToClasses = (classes: QuantityClass[], ramp: { id: string; colors: string[] }): QuantityClass[] => {
  const colors = rampColors(ramp, classes.length);
  return classes.map((c, i) => {
    const color = colors[i] ?? colors[colors.length - 1];
    return { ...c, color, symbology: { ...c.symbology, fill: color } };
  });
};

export const DEFAULT_RAMP = COLOR_RAMPS[0];

export const defaultQuantityConfig = (): QuantitySymbologyConfig => ({
  type: 'quantity',
  field: '',
  breakType: 'jenks',
  numberOfClasses: 5,
  colorRamp: DEFAULT_RAMP,
  classes: [],
});
